import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, CardContent } from '@mui/joy';
import {
  Backdrop,
  Box,
  Button,
  Fade,
  Grid,
  Modal,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import { useTranslation } from 'react-i18next';
import { GENERAL_FONT, MHColours } from '../../constants/theme';
import { formatDate } from '../../lib/helpers/date';
import { ICalendarEvent, IDiaryEntry } from '../../lib/interfaces';
import {
  loadEvents,
  mapColorToEntrie,
  mapTextToEntrie,
} from '../../lib/helpers/diary';
import mhLogoPng from '../../../public/image/mh_logo.png';

export interface SimpleDialogProps {
  onClick?: (value?: string) => void;
  dayEntry?: IDiaryEntry;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  p: 4,
  borderRadius: '5px',
};

const cardSx = {
  backgroundColor: MHColours.mhGreenLight,
  mr: '20px',
  maxWidth: '300px',
};

const cardContentSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-start' as const,
  gap: 1,
  pb: 2,
};

const dateTextSx = {
  fontSize: 18,
  fontWeight: 'bold',
  mt: '12px',
  fontFamily: GENERAL_FONT,
};

const inputsLabelSx = {
  fontFamily: GENERAL_FONT,
  fontWeight: 'bold',
  color: MHColours.mhGreen,
};

const migraineListSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-start' as const,
  gap: 0,
  mt: 0.5,
};

const viewDiaryButtonSx = {
  textTransform: 'none' as const,
};

const viewDiaryButtonStyle = {
  padding: '6px 16px',
  borderRadius: '4px',
  marginTop: '16px',
  backgroundColor: MHColours.mhGreen,
  fontFamily: GENERAL_FONT,
  color: 'white',
  maxWidth: '115px',
  alignSelf: 'flex-start' as const,
};

const modalHeaderSx = {
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'flex-start' as const,
  justifyContent: 'space-between',
  mb: 3,
};

const modalHeaderRightSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-end' as const,
  gap: 0.5,
};

const modalMigraineColumnSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-start' as const,
  gap: 0,
  pt: 0,
  mt: 0,
};

export const loadMigraineType = (color: string, text: string, id: string) => (
  <Box
    key={id}
    sx={{
      display: 'grid',
      gridTemplateColumns: '20px 1fr',
      alignItems: 'center',
      columnGap: 1.2,
      py: 0.5,
      width: '100%',
    }}
  >
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        backgroundColor: color,
      }}
    />
    <Typography
      sx={{
        fontFamily: GENERAL_FONT,
        fontWeight: 'bold',
        fontSize: '15px',
        lineHeight: 1.3,
      }}
    >
      {text}
    </Typography>
  </Box>
);

const DayDetailsCard: React.FC<SimpleDialogProps> = ({ onClick, dayEntry }) => {
  const [calendarEvent, setCalendarEvent] = useState<ICalendarEvent | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!dayEntry) {
      setCalendarEvent(null);
      return;
    }
    const event = loadEvents([dayEntry])[0];
    setCalendarEvent(event);
  }, [dayEntry]);

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => setModalOpen(false);

  const loadMigraines = () => {
    if (!calendarEvent) return null;

    return calendarEvent.entries.map((entry) =>
      loadMigraineType(
        mapColorToEntrie(entry),
        mapTextToEntrie(entry),
        `${calendarEvent.id}-${entry}`
      )
    );
  };

  const formattedDate = dayEntry
    ? formatDate(dayjs(dayEntry.diaryDate).toString())
    : '';

  return (
    <>
      <Card variant="plain" sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <Typography sx={dateTextSx}>{formattedDate}</Typography>

          <Typography sx={inputsLabelSx}>{t('inputs')}</Typography>

          <Box sx={migraineListSx}>{loadMigraines()}</Box>

          <Button
            sx={viewDiaryButtonSx}
            style={viewDiaryButtonStyle}
            onClick={() => {
              handleModalOpen();
              if (onClick) onClick();
            }}
          >
            {t('viewDiary')}
          </Button>
        </CardContent>
      </Card>

      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={modalOpen}>
          <Box sx={modalStyle}>
            <Box sx={modalHeaderSx}>
              <Box component="img" src={mhLogoPng} sx={{ width: '130px' }} />
              <Box sx={modalHeaderRightSx}>
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    fontFamily: GENERAL_FONT,
                  }}
                >
                  {t('overview')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontFamily: GENERAL_FONT,
                  }}
                >
                  <span style={{ fontWeight: 'bold' }}>{t('date')}: </span>
                  {formattedDate}
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6} md={6} sx={modalMigraineColumnSx}>
                {loadMigraines()}
              </Grid>
              <Grid
                item
                xs={6}
                md={6}
                sx={{
                  whiteSpace: 'pre-line',
                  fontFamily: GENERAL_FONT,
                }}
              >
                {calendarEvent?.title?.trim()}
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default DayDetailsCard;
